import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useModal } from '../../hooks/useModal';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { useSlashSearchShortcut } from '../../hooks/useSlashSearchShortcut';
import { intentLabel } from '../../shared/intent';
import { formatPhone } from '../../shared/phone';
import {
  Breakdown,
  BreakdownSkeleton,
  Button,
  Callout,
  Dialog,
  EmptyState,
  Fact,
  IconArrowRight,
  IconPhone,
  IconSearch,
  Input,
  Kbd,
  Meter,
  ListPage,
  PageHeaderBand,
  Pagination,
  QueryGate,
  Skeleton,
  Spinner,
  SummaryFact,
  SummaryFacts,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TranscriptView,
  When,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  type CallIntelligence,
  callReasonLinkCount,
  intelligenceRows,
} from '../Dashboard/dashboardModel';
import { useCallIntelligence } from '../Dashboard/dashboardQueries';

interface CallRecord {
  id: number;
  event?: string;
  call_id?: string;
  direction?: string;
  call_status?: string;
  call_status_reason?: string;
  from_number?: string;
  to_number?: string;
  duration_ms?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  assistant_id?: number | null;
  store_id?: number | null;
  store_name?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  is_vip?: boolean;
  vip_name?: string | null;
  primary_intent?: string | null;
  secondary_intent?: string | null;
  transcript?: string | null;
}

interface CallsResponse {
  results?: CallRecord[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  next?: string | null;
  previous?: string | null;
  /** Sum of call durations (minutes); used with count for avg when no explicit avg field */
  total_minutes?: number;
  average_duration_ms?: number;
  avg_duration_ms?: number;
  average_call_duration_seconds?: number;
}

const PAGE_SIZE = 20;

const formatDurationMs = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatDuration = (
  durationMs?: number | null,
  start?: string | null,
  end?: string | null,
) => {
  let ms = durationMs ?? null;
  if (ms === null && start && end) {
    const diff = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isNaN(diff) && diff >= 0) {
      ms = diff;
    }
  }

  if (ms === null) return '—';

  return formatDurationMs(ms);
};

/** Bars are scaled against a fixed 5-minute ceiling so rows compare across pages. */
const DURATION_BAR_MAX_MS = 5 * 60 * 1000;

function callDurationMs(call: CallRecord): number | null {
  if (call.duration_ms != null && call.duration_ms >= 0) {
    return call.duration_ms;
  }
  if (call.start_time && call.end_time) {
    const diff =
      new Date(call.end_time).getTime() - new Date(call.start_time).getTime();
    if (!Number.isNaN(diff) && diff >= 0) return diff;
  }
  return null;
}

function averageDurationLabel(
  payload: CallsResponse | CallRecord[],
  totalCount: number,
  pageCalls: CallRecord[],
): string {
  const d = Array.isArray(payload)
    ? ({} as CallsResponse)
    : (payload as CallsResponse);

  const msDirect = d.average_duration_ms ?? d.avg_duration_ms;
  if (msDirect != null && Number.isFinite(msDirect) && msDirect >= 0) {
    return formatDurationMs(msDirect);
  }
  const sec = d.average_call_duration_seconds;
  if (sec != null && Number.isFinite(sec) && sec >= 0) {
    return formatDurationMs(sec * 1000);
  }
  if (
    typeof d.total_minutes === 'number' &&
    d.total_minutes >= 0 &&
    totalCount > 0
  ) {
    return formatDurationMs((d.total_minutes / totalCount) * 60 * 1000);
  }

  const durations = pageCalls
    .map(callDurationMs)
    .filter((x): x is number => x != null);
  if (durations.length === 0) return '—';
  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  return formatDurationMs(avg);
}

interface CallsQueryResult {
  calls: CallRecord[];
  totalPages: number;
  totalCount: number;
  averageDurationDisplay: string;
  /** The search this page was fetched with; placeholder data keeps the previous one. */
  search: string;
}

/** UI column key -> API `ordering` field name allowed by `RetellCallListView`. */
const CALLS_SORT_UI_TO_FIELD: Record<string, string> = {
  id: 'id',
  start_time: 'start_time',
  customer_name: 'customer_name',
  customer_id: 'customer_id',
  from_number: 'from_number',
  duration_ms: 'duration_ms',
  created_at: 'created_at',
};

const CALLS_SORT_FIELD_TO_UI: Record<string, string> = Object.fromEntries(
  Object.entries(CALLS_SORT_UI_TO_FIELD).map(([k, v]) => [v, k]),
);

const DEFAULT_CALLS_ORDERING = '-created_at';

async function fetchCallsPage(
  page: number,
  ordering: string,
  search: string,
  callId: string,
): Promise<CallsQueryResult> {
  const response = await api.get(
    apiEndpoints.getCalls(page, PAGE_SIZE, true, undefined, ordering, {
      search: search || undefined,
      callId: callId || undefined,
    }),
  );

  if (!response) {
    throw new Error('Failed to fetch calls.');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch calls (status ${response.status}). ${text.substring(0, 120)}`,
    );
  }

  const data: CallsResponse | CallRecord[] = await response.json();

  let results: CallRecord[] = [];
  let nextTotalPages = 1;
  let count = 0;

  if (Array.isArray(data)) {
    results = data;
    count = data.length;
  } else {
    results = Array.isArray(data.results) ? data.results : [];
    count = typeof data.count === 'number' ? data.count : results.length;

    if (typeof data.total_pages === 'number') {
      nextTotalPages = data.total_pages;
    } else if (count > 0) {
      nextTotalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
    }
  }

  return {
    calls: results,
    totalPages: nextTotalPages,
    totalCount: count,
    averageDurationDisplay: averageDurationLabel(data, count, results),
    search,
  };
}

/**
 * Caller is the best identity we have: a name, otherwise the number.
 * Intent is the call fact's primary reason. Identifiers stay after the
 * time and duration; the transcript action closes the row.
 */
const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  sortable?: boolean;
}[] = [
  { key: 'customer_name', label: 'Caller', width: 240 },
  { key: 'intent', label: 'Intent', width: 148, sortable: false },
  { key: 'start_time', label: 'Time', width: 168 },
  { key: 'duration_ms', label: 'Duration', width: 120 },
  { key: 'id', label: 'Call ID', width: 96 },
  { key: 'customer_id', label: 'Customer ID', width: 112 },
];

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

/**
 * The same call-intelligence breakdown as the dashboard, under the list.
 * It reads the store-wide reasons, so search and paging on the table
 * don't change it.
 */
const CallReasonsChart: React.FC = () => {
  const query = useCallIntelligence();
  const tagged = query.data?.tagged_calls ?? 0;
  const callCount = query.data ? callReasonLinkCount(query.data) : 0;
  return (
    <section
      aria-labelledby="calls-reasons"
      className="border-t border-v2-line p-5 lg:p-6"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2
          id="calls-reasons"
          className="text-v2-18 font-bold tracking-[-0.015em] text-v2-fg"
        >
          Call reasons
        </h2>
        {tagged > 0 && (
          <span className="v2-num text-v2-13 text-v2-fg-3">
            {tagged.toLocaleString()} {tagged === 1 ? 'call' : 'calls'} tagged
          </span>
        )}
        {query.data && (
          <span className="ml-auto inline-flex h-6 items-center rounded-full bg-v2-sunken px-2.5 text-v2-11 font-semibold text-v2-fg-2">
            {query.data.scope === 'all_time' ? 'All time' : 'This period'}
          </span>
        )}
      </div>
      <div className="mt-5">
        <QueryGate
          query={query}
          errorTitle="Call reasons could not be loaded."
          skeleton={<BreakdownSkeleton />}
        >
          {(intel: CallIntelligence | null) => {
            const rows = intel ? intelligenceRows(intel) : [];
            if (rows.length === 0) {
              return (
                <EmptyState
                  compact
                  icon={<IconPhone />}
                  title="No calls tagged yet"
                  description="Arthur sorts every call by why the guest rang."
                />
              );
            }
            return (
              <div>
                <Breakdown
                  rows={rows}
                  noun="calls"
                  palette="multi"
                  size="lg"
                  aria-label="Call reasons"
                />
                {callCount > 0 && (
                  <Link
                    to="/calls"
                    className="v2-num mt-2 inline-flex h-7 items-center gap-1 rounded-v2 px-1.5 text-v2-12 font-medium text-v2-fg-3 hover:bg-v2-sunken hover:text-v2-accent"
                  >
                    {callCount.toLocaleString()}{' '}
                    {callCount === 1 ? 'call' : 'calls'}
                    <IconArrowRight size={12} />
                  </Link>
                )}
              </div>
            );
          }}
        </QueryGate>
      </div>
    </section>
  );
};

const CallsTable: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam = searchParams.get('ordering') ?? DEFAULT_CALLS_ORDERING;
  const callId = searchParams.get('call_id')?.trim() ?? '';
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashSearchShortcut(searchRef);

  const { sortKey, sortOrder } = useMemo(() => {
    const raw = orderingParam;
    const desc = raw.startsWith('-');
    const field = desc ? raw.slice(1) : raw;
    const uiKey = CALLS_SORT_FIELD_TO_UI[field] ?? 'created_at';
    return {
      sortKey: uiKey,
      sortOrder: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
    };
  }, [orderingParam]);

  const [page, setPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  const { isOpen: isTranscriptOpen, openModal, closeModal } = useModal();
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const queryKey = useScopedQueryKey(
    'calls',
    page,
    PAGE_SIZE,
    orderingParam,
    debouncedSearch,
    callId,
  );
  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () => fetchCallsPage(page, orderingParam, debouncedSearch, callId),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      setDebouncedSearch(next);
      // Drop the deep link only once a search is actually sent. Replace the
      // current entry: a push would let Back restore call_id while the search
      // box is still filled, and the effect would remove it again.
      if (next && callId) {
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            params.delete('call_id');
            return params;
          },
          { replace: true },
        );
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, callId, setSearchParams]);

  const toggleSort = useCallback(
    (key: string) => {
      const targetField = CALLS_SORT_UI_TO_FIELD[key];
      if (!targetField) return;
      setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const prevRaw = prev.get('ordering') ?? DEFAULT_CALLS_ORDERING;
        const prevDesc = prevRaw.startsWith('-');
        const prevField = prevDesc ? prevRaw.slice(1) : prevRaw;
        const newDesc = prevField !== targetField ? false : !prevDesc;
        const param = newDesc ? `-${targetField}` : targetField;
        next.set('ordering', param);
        return next;
      });
    },
    [setSearchParams],
  );

  const calls = data?.calls ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const averageDurationDisplay = data?.averageDurationDisplay ?? '—';
  const showInitialLoading = isPending && calls.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'An unexpected error occurred while fetching calls.'
        : null;

  const handleOpenTranscript = (call: CallRecord) => {
    setSelectedCall(call);
    openModal();
  };

  const handleCloseTranscript = () => {
    setSelectedCall(null);
    closeModal();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    setPage(1);
  };

  const hasAverageDurationData =
    averageDurationDisplay !== '—' && averageDurationDisplay.trim() !== '';
  const showCallCount = showInitialLoading || (!fetchError && totalCount > 0);
  const showAverageDuration = showInitialLoading || hasAverageDurationData;
  const showPagination = !showInitialLoading && !fetchError && calls.length > 0;
  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && calls.length > 0;
  useEffect(() => {
    if (isTranscriptOpen && transcriptRef.current) {
      transcriptRef.current.scrollTop = 0;
    }
  }, [isTranscriptOpen, selectedCall]);

  return (
    <>
      <PageMeta title="AskArthur | Calls" description="Call history" />

      <ListPage
        title="Calls"
        actions={
          isRefreshing ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
            >
              <Spinner />
              Updating calls...
            </span>
          ) : null
        }
        summary={
          (showCallCount || showAverageDuration) && (
            <SummaryFacts>
              {showCallCount && (
                <SummaryFact
                  label={data?.search ? 'Matching calls' : 'Calls'}
                  value={
                    showInitialLoading ? (
                      <Skeleton width={48} className="h-7" />
                    ) : (
                      totalCount.toLocaleString()
                    )
                  }
                />
              )}
              {showAverageDuration && (
                <SummaryFact
                  label="Avg. duration"
                  value={
                    showInitialLoading ? (
                      <Skeleton width={32} className="h-7" />
                    ) : (
                      averageDurationDisplay
                    )
                  }
                />
              )}
            </SummaryFacts>
          )
        }
        toolbar={
          <PageHeaderBand compact>
            <div className="w-full min-w-0 self-center sm:w-[340px] sm:shrink-0">
              <Input
                ref={searchRef}
                id="calls-search"
                type="search"
                aria-label="Search by name, phone, or ID"
                placeholder="Find a call by name, phone, or ID…"
                value={searchInput}
                onChange={handleSearchChange}
                leading={<IconSearch size={14} />}
                trailing={
                  <span className="hidden sm:inline-flex">
                    <Kbd>/</Kbd>
                  </span>
                }
              />
            </div>
          </PageHeaderBand>
        }
      >
        {fetchError ? (
          <div className="p-4 lg:p-6">
            <Callout tone="danger" title="Couldn't load calls">
              {fetchError}
            </Callout>
          </div>
        ) : !showInitialLoading && calls.length === 0 ? (
          <EmptyState
            icon={<IconPhone />}
            title={
              callId
                ? "That call isn't in this list"
                : `You don't have any ${debouncedSearch ? 'matching calls' : 'calls'} at the moment`
            }
            description={
              callId
                ? 'It may belong to another store, or the link is out of date.'
                : `When ${debouncedSearch ? 'matching calls' : 'calls'} appear, they'll show up here.`
            }
          />
        ) : (
          <>
            {showInitialLoading && (
              <p role="status" className="sr-only">
                Loading calls…
              </p>
            )}
            <div
              className={clsx(
                'v2-scroll overflow-x-auto transition-opacity',
                isRefreshing && 'opacity-70',
              )}
            >
              <Table fixed className="min-w-[960px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        sortable={
                          column.sortable !== false && !showInitialLoading
                        }
                        active={sortKey === column.key}
                        dir={sortOrder}
                        onSort={() => toggleSort(column.key)}
                      >
                        {column.label}
                      </TH>
                    ))}
                    <TH align="right">Transcript</TH>
                  </tr>
                </THead>
                <TBody>
                  {showInitialLoading ? (
                    <TableSkeletonRows cols={COLUMNS.length + 1} />
                  ) : (
                    calls.map((call) => {
                      const formattedDuration = formatDuration(
                        call.duration_ms,
                        call.start_time,
                        call.end_time,
                      );
                      const intent = intentLabel(call.primary_intent);
                      const phone = formatPhone(call.from_number);
                      const name = call.customer_name?.trim();
                      return (
                        <TR
                          key={call.id}
                          className="!h-auto !border-v2-line/50 hover:!bg-v2-sunken"
                        >
                          <TD className="!h-auto overflow-hidden py-[13px]">
                            <div className="flex min-w-0 flex-col justify-center">
                              {name ? (
                                <div className="truncate font-semibold text-v2-fg">
                                  {name}
                                </div>
                              ) : phone ? (
                                <div className="v2-num truncate font-semibold text-v2-fg">
                                  {phone}
                                </div>
                              ) : (
                                <div className="truncate font-semibold text-v2-fg">
                                  Private number
                                </div>
                              )}
                              {name && phone ? (
                                <div className="v2-num mt-0.5 truncate text-v2-12 font-normal text-v2-fg-2">
                                  {phone}
                                </div>
                              ) : null}
                              {call.is_vip && (
                                <div className="mt-1">
                                  <span
                                    title={
                                      call.vip_name
                                        ? `VIP: ${call.vip_name}`
                                        : 'VIP guest'
                                    }
                                  >
                                    <Tag tone="warning">VIP</Tag>
                                  </span>
                                </div>
                              )}
                            </div>
                          </TD>
                          <TD className="!h-auto py-[13px]">
                            {intent ? (
                              <Tag tone="neutral" quiet>
                                {intent}
                              </Tag>
                            ) : (
                              <MissingValue />
                            )}
                          </TD>
                          <TD
                            num
                            className="!h-auto whitespace-nowrap py-[13px]"
                          >
                            <When value={call.start_time} />
                          </TD>
                          <TD num className="!h-auto py-[13px]">
                            {formattedDuration === '—' ? (
                              <MissingValue />
                            ) : (
                              <span className="inline-flex min-w-[90px] flex-col gap-1.5">
                                <span>{formattedDuration}</span>
                                <Meter
                                  value={
                                    (callDurationMs(call) ?? 0) /
                                    DURATION_BAR_MAX_MS
                                  }
                                  className="!h-[5px] !w-[90px] !bg-v2-sunken"
                                  fillClassName="bg-v2-success"
                                  barProps={{
                                    'data-testid': 'call-duration-bar',
                                  }}
                                />
                              </span>
                            )}
                          </TD>
                          <TD
                            num
                            className="!h-auto py-[13px] text-v2-12 text-v2-fg-3"
                          >
                            #{call.id}
                          </TD>
                          <TD
                            num
                            className="!h-auto py-[13px] text-v2-12 text-v2-fg-3"
                          >
                            {call.customer_id ? (
                              call.customer_id
                            ) : (
                              <MissingValue />
                            )}
                          </TD>
                          <TD align="right" className="!h-auto py-[13px]">
                            {call.transcript ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                aria-label={`View transcript for call #${call.id}`}
                                onClick={() => handleOpenTranscript(call)}
                                className="!rounded-full px-3.5"
                              >
                                View
                              </Button>
                            ) : (
                              <MissingValue />
                            )}
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </div>
            {showPagination && (
              <Pagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPage={setPage}
                noun="calls"
              />
            )}
          </>
        )}
        <CallReasonsChart />
      </ListPage>

      <Dialog
        open={isTranscriptOpen}
        onClose={handleCloseTranscript}
        title="Call transcript"
        description={selectedCall ? `Call #${selectedCall.id}` : undefined}
        size="lg"
      >
        {selectedCall ? (
          <>
            <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3 sm:grid-cols-3">
              <Fact
                label="Duration"
                value={formatDuration(
                  selectedCall.duration_ms,
                  selectedCall.start_time,
                  selectedCall.end_time,
                )}
              />
              <Fact
                label="From"
                value={selectedCall.from_number || '—'}
                missing={!selectedCall.from_number}
              />
              <Fact
                label="To"
                value={selectedCall.to_number || '—'}
                missing={!selectedCall.to_number}
              />
            </dl>

            <div
              ref={transcriptRef}
              role="region"
              aria-label="Transcript"
              tabIndex={0}
              className="v2-scroll mt-4 max-h-[50vh] overflow-y-auto pr-2 sm:max-h-[60vh]"
            >
              <TranscriptView
                transcript={selectedCall.transcript}
                guestLabel={
                  selectedCall.customer_name?.split(' ')[0] || 'Caller'
                }
              />
            </div>
          </>
        ) : (
          <p className="text-v2-12 text-v2-fg-3">Transcript not available.</p>
        )}
      </Dialog>
    </>
  );
};

export default CallsTable;
